/**
 * Small wrappers around TanStack Router primitives that loosen the
 * strict per-route typing. We don't use the file-based router plugin
 * (we wire routes programmatically in main.tsx), so the auto-inferred
 * `to` and `params` types lag behind reality.
 *
 * When we add the @tanstack/router-vite-plugin and a routeTree.gen.ts,
 * these wrappers go away and Link/useParams get end-to-end typing.
 */
import {
  Link as RouterLink,
  useNavigate as routerUseNavigate,
  useParams as routerUseParams,
} from '@tanstack/react-router'
import type { ComponentProps } from 'react'

type LooseLinkProps = Omit<ComponentProps<typeof RouterLink>, 'to' | 'params'> & {
  to: string
  params?: Record<string, string>
}

export function Link(props: LooseLinkProps) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return <RouterLink {...(props as any)} />
}

/**
 * Params for the current route.
 *
 * Callers pass `{ from: '/cases/$caseId' }` for readability — it documents
 * which route the component belongs to — but the value is deliberately NOT
 * forwarded. Because routes are wired programmatically here rather than
 * generated by the file-based plugin, a route's id does not equal its path
 * template, so `from` matches nothing and TanStack throws
 * "Invariant failed: Could not find an active match from ...". Every param
 * route in the app hit that: /cases/$caseId, /ingest/$batchId,
 * /trips/$tripId and /tele/console/$caseId alike.
 *
 * `strict: false` reads the params off the closest active match instead,
 * which is what these call sites actually want. Same reason CasesPage uses
 * `useSearch({ strict: false })`.
 */
export function useParams<T extends Record<string, string>>(_opts?: { from?: string }): T {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return routerUseParams({ strict: false } as any) as T
}

/**
 * Same loosening for programmatic navigation.
 *
 * `to` is matched against the ROUTE TREE, not treated as a resolved URL, so a
 * param route must be given its pattern plus params:
 *
 *   navigate({ to: '/tele/console/$caseId', params: { caseId } })
 *
 * Interpolating the id into the string instead — `/tele/console/abc-123` —
 * type-checks and then finds no matching route at runtime.
 */
export function useNavigate() {
  const navigate = routerUseNavigate()
  return (opts: { to: string; params?: Record<string, string>; search?: unknown; replace?: boolean }) =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    navigate(opts as any)
}
